import { InputWithIcon } from '@/components/ui/vehicle/input-with-icon';
import { toast } from '@/hooks/use-toast';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { vehicleListingStore } from '@/models/stores/vehicle-listings';
import type { VehicleListing } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { Route } from 'lucide-react';

export type BatteryWarrantyMilesInputProps = {
  id: string;
  value: VehicleListing['remaining_battery_warranty_miles'];
  className?: string;
};

export function BatteryWarrantyMilesInput({
  id,
  value,
  className,
}: BatteryWarrantyMilesInputProps) {
  const updateRemainingBatteryWarrantyMilesMutation = useMutation({
    onMutate: async ({ remainingBatteryWarrantyMiles }) => {
      vehicleListingStore.updateRemainingBatteryWarrantyMiles({
        id,
        remainingBatteryWarrantyMiles,
      });
    },
    mutationFn: async ({
      remainingBatteryWarrantyMiles,
    }: {
      remainingBatteryWarrantyMiles: VehicleListing['remaining_battery_warranty_miles'];
    }) => {
      return await pdkAxios.post(
        `/inventory/listings/${id}/remaining_battery_warranty_miles`,
        {
          remaining_battery_warranty_miles: remainingBatteryWarrantyMiles,
        },
      );
    },
    onError: (error) => {
      toast({
        title: 'Error updating remaining battery warranty miles',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleRemainingBatteryWarrantyMilesChange = (
    newRemainingBatteryWarrantyMiles: string,
  ) => {
    // Handle empty string or invalid input
    if (
      newRemainingBatteryWarrantyMiles === '' ||
      isNaN(parseInt(newRemainingBatteryWarrantyMiles))
    ) {
      updateRemainingBatteryWarrantyMilesMutation.mutate({
        remainingBatteryWarrantyMiles: 0,
      });
    } else {
      updateRemainingBatteryWarrantyMilesMutation.mutate({
        remainingBatteryWarrantyMiles: parseInt(
          newRemainingBatteryWarrantyMiles,
        ),
      });
    }
  };

  return (
    <div>
      <InputWithIcon
        icon={<Route className="h-4 w-4" />}
        value={value?.toString() || ''}
        placeholder="Remaining battery warranty miles"
        onChange={handleRemainingBatteryWarrantyMilesChange}
        className={cn('pl-0', className)}
        inputClassName="w-14"
        suffix="miles"
      />
    </div>
  );
}
